/**
 * MKS-410 — the unified queue's filter bar.
 *
 * Every control is a controlled select whose value comes from the URL and
 * whose onChange writes back to the URL. The page owns the URL; this component
 * owns NO state, which is MKS-339's deliberate design and what makes a
 * filtered inbox a shareable link that survives a reload.
 *
 * `project` and `assignee` each carry two different kinds of value — an id, or
 * the literal `untriaged` / `unassigned`. That is the backend's own
 * querystring contract, so the options below mix them in one select rather
 * than adding a second "unassigned?" checkbox the URL would have to encode
 * separately.
 */
import { DropdownSelect } from '../ui/DropdownSelect';
import { Btn } from '../ui/Btn';
import { REQUEST_STATUS_TONES, requestSourceTone } from './requestFormat';
import {
  HELPDESK_REQUEST_SOURCES,
  HELPDESK_REQUEST_STATUSES,
  type HelpdeskRequestFilters,
  type HelpdeskRequestType,
} from '../../services/api/helpdeskRequests';
import type { Customer, ProjectListItem } from '../../services/api/projects';
import type { AdminUser } from '../../services/api/admin';

export interface RequestQueueFiltersProps {
  filters: HelpdeskRequestFilters;
  types: HelpdeskRequestType[];
  projects: ProjectListItem[];
  customers: Customer[];
  people: AdminUser[];
  onChange: (key: keyof HelpdeskRequestFilters, value: string) => void;
  onReset: () => void;
}

function personLabel(user: AdminUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name.length > 0 ? name : user.email;
}

export function RequestQueueFilters({
  filters,
  types,
  projects,
  customers,
  people,
  onChange,
  onReset,
}: RequestQueueFiltersProps) {
  const hasAnyFilter = Object.values(filters).some((v) => v !== undefined && v !== '');

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="request-queue-filters">
      <DropdownSelect
        aria-label="Status"
        value={filters.status ?? ''}
        onChange={(e) => onChange('status', e.target.value)}
      >
        <option value="">Any status</option>
        {HELPDESK_REQUEST_STATUSES.map((s) => (
          <option key={s} value={s}>
            {REQUEST_STATUS_TONES[s].label}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Source"
        value={filters.source ?? ''}
        onChange={(e) => onChange('source', e.target.value)}
      >
        <option value="">Internal + client</option>
        {HELPDESK_REQUEST_SOURCES.map((s) => (
          <option key={s} value={s}>
            {requestSourceTone(s).label}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Type"
        value={filters.typeId ?? ''}
        onChange={(e) => onChange('typeId', e.target.value)}
      >
        <option value="">Any type</option>
        {types.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Project"
        value={filters.project ?? ''}
        onChange={(e) => onChange('project', e.target.value)}
      >
        <option value="">Any project</option>
        <option value="untriaged">Not yet routed</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Customer"
        value={filters.customerId ?? ''}
        onChange={(e) => onChange('customerId', e.target.value)}
      >
        <option value="">Any customer</option>
        {customers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </DropdownSelect>

      <DropdownSelect
        aria-label="Owner"
        value={filters.assignee ?? ''}
        onChange={(e) => onChange('assignee', e.target.value)}
      >
        <option value="">Any owner</option>
        <option value="unassigned">Unassigned</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {personLabel(p)}
          </option>
        ))}
      </DropdownSelect>

      {/* The "ticket closed, nobody wrote the note" view — the requests
          holding the ISO record open. A single toggle rather than a select:
          the backend's own querystring only accepts `true`. */}
      <DropdownSelect
        aria-label="Resolution note"
        value={filters.needsResolutionNote ?? ''}
        onChange={(e) => onChange('needsResolutionNote', e.target.value)}
      >
        <option value="">Any</option>
        <option value="true">Needs a resolution note</option>
      </DropdownSelect>

      {hasAnyFilter && (
        <Btn variant="ghost" size="sm" onClick={onReset}>
          Reset
        </Btn>
      )}
    </div>
  );
}
